import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import Const from '../Consts'
import { AuthContext, USER_TYPES, verifiedAgencyUser } from 'components/common/auth/AuthContext'
import ReviewShape from './prop_shapes/review'
import ReviewMetadataShape from './prop_shapes/reviewMetadata'
import SupplierShape from './prop_shapes/supplier'
import AutoPubCondensedReviewShape from './prop_shapes/autoPubCondensedReview'
import AutoPubCondensedReview from './AutoPubCondensedReview'
import ReviewOneClickRatingArea from './ReviewOneClickRatingArea'
import { StarRating } from '../review_ratings'
import { ReviewTiles } from '../review_tiles'
import { HiddenReviews } from './hidden_reviews'
import { RecentPerformance } from './RecentPerformance'

const RatingRow = ({ children, label }) => {
  return (
    <div>
      <div className='grid-x margin-bottom-1'>
        <div className='cell small-6 medium-12 large-7 pro-text-m color-pro-steel-grey-600'>
          <span className='padding-right-05'>
            {label}
          </span>
        </div>
        <div className='cell small-6 medium-12 large-5'>
          {children}
        </div>
      </div>
    </div>
  )
}

RatingRow.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string.isRequired
}

const Reviews = (props) => {
  const authContext = useContext(AuthContext)
  const viewingAsSupplier = () => authContext.userType === USER_TYPES.SUPPLIER_USER

  const showAutoCondensedReviews = () => {
    return !!props.autoPubCondensedReview && verifiedAgencyUser(authContext)
  }

  const autoPubCondensedReviews = showAutoCondensedReviews() ? props.autoPubCondensedReview.auto_pub_condensed_reviews : null

  const renderRatings = () => {
    return Object.entries(Const.OTHER_RATING_TYPES).map(([key, label]) => {
      const starAmount = props.metadata.star_ratings[key]
      const canSeeReviews = verifiedAgencyUser(authContext) || authContext.isOwner

      return (
        <div key={`rating-${key}`}>
          <RatingRow label={label}>
            <StarRating rating={starAmount} blurred={!canSeeReviews} className='other-ratings' />
          </RatingRow>
        </div>
      )
    })
  }

  const renderFilters = () => (
    <div className='cell small-12 medium-6 margin-horizontal-2 margin-vertical-1'>
      {props.reviewFilters}
    </div>
  )

  const renderRealReviews = () => (
    <>
      {renderFilters()}
      <ReviewTiles reviews={props.reviews} />
    </>
  )

  const renderReviews = () => {
    if (verifiedAgencyUser(authContext) || authContext.isOwner || authContext.userIsGooglebot) {
      return renderRealReviews()
    } else {
      return <HiddenReviews {...props} />
    }
  }

  const renderStarPercentage = (rating) => {
    const percentage = (((props.metadata.review_counts[rating] || 0) / props.metadata.review_count || 0) * 100).toFixed()
    const styling = {
      width: `${percentage}%`
    }
    return (
      <div className='grid-x align-middle margin-bottom-1'>
        <div className='cell small-3 medium-3 pro-text-m color-pro-steel-grey-700'>{rating} star</div>
        <div className='cell small-7 medium-6'>
          <div
            className='pro-progress progress'
            tabIndex='0'
            aria-valuenow={percentage}
            aria-valuemin='0'
            aria-valuetext={`${percentage} percent`}
            aria-valuemax='100'
          >
            <div className='progress-meter' style={styling} />
          </div>
        </div>
        <div className='cell small-2 medium-3 text-right pro-text-m color-pro-steel-grey-500'>
          {percentage}%
        </div>
      </div>
    )
  }

  const renderStarPercentages = () => {
    return [5, 4, 3, 2, 1].map((rating) => {
      return (
        <div key={`rating-${rating}`}>
          {renderStarPercentage(rating)}
        </div>
      )
    })
  }

  const renderRecentPerformance = () => {
    if (props.supplier.reviewsSummary) {
      return (
        <RecentPerformance
          reviewsSummary={props.supplier.reviewsSummary}
          reviewsSummaryLastUpdatedAt={props.supplier.reviewsSummaryLastUpdatedAt}
        />
      )
    }
  }

  return (
    <>
      <div className='show-for-small-only section-header-content'>
        {props.reviewButtons}
      </div>

      {authContext.isOwner &&
        <div className='grid-x section-header-content'>
          <div className='cell shrink'>
            <i className='far fa-exclamation-square color-pro-red-500 padding-right-1' />
          </div>
          <div className='cell auto'>
            Reviews made visible to you by buyers on Procurated are not allowed to be copied, shared, or distributed.
            See our <a href='/terms'>Terms</a> for additional information.
          </div>
        </div>}
      <div className='grid-x pro-rounded margin-top-32 review-metadata pro-close-shadow'>
        <div className='cell small-12 medium-6 padding-2 supplier-ratings'>
          <div className='pro-head-6 font-bold'>Overall Rating</div>
          <div className='star-rating-container overall-rating margin-top-1'>
            <div className={`star-rating stars-${props.metadata.star_ratings.overall_rating}`} />
          </div>
          <div className='pro-head-5 color-pro-steel-grey-600 supplier-rating'>
            {props.metadata.review_count > 0 ? `${props.metadata.rating} out of 5` : ''}
          </div>
          <div className='pro-text-m color-pro-steel-grey-600'>
            {props.metadata.review_count} total ratings & reviews
          </div>

          <div className='margin-top-2'>
            {renderStarPercentages()}
          </div>
        </div>
        <div className='cell small-12 medium-6 padding-2 supplier-ratings'>
          {renderRatings()}
        </div>
        {renderRecentPerformance()}
        {viewingAsSupplier() ? null : <ReviewOneClickRatingArea supplier={props.supplier} />}
      </div>

      <hr className='margin-top-3 hide-for-small-only' />

      {renderReviews()}

      {showAutoCondensedReviews() &&
        <AutoPubCondensedReview
          count={autoPubCondensedReviews?.count}
          starRating={autoPubCondensedReviews?.star_rating}
          rating={autoPubCondensedReviews?.rating}
        />}
    </>
  )
}

Reviews.propTypes = {
  autoPubCondensedReview: AutoPubCondensedReviewShape,
  callout: PropTypes.string.isRequired,
  metadata: ReviewMetadataShape.isRequired,
  procuratedPromisePath: PropTypes.string.isRequired,
  reviewButtons: PropTypes.node,
  reviewFilters: PropTypes.node,
  reviews: PropTypes.arrayOf(ReviewShape),
  supplier: SupplierShape.isRequired
}

export default Reviews
